import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message, Modal } from 'antd'
import {
  VcnManagementTopbar,
  EXPORT_ALL_LIMIT,
} from '../../components/VcnManagement/VcnManagementTopbar'
import { VcnManagementContent } from '../../components/VcnManagement/VcnManagementContent'
import { VcnManagementBenefitSidebar } from '../../components/VcnManagement/VcnManagementBenefitSidebar'
import { VcnManagementBulkUploadModal } from '../../components/VcnManagement/VcnManagementBulkUploadModal'
import { VcnProviderRadio } from '../../components/VcnManagement/VcnProviderRadio'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { useVcnProviderAvailability } from 'modules/virtual-cards/useVcnProviderAvailability'
import { VcnBulkJobState } from '../../api/dto/vcn-bulk-job.dto'
import type { VcnBulkJobStatus } from '../../api/dto/vcn-bulk-job.dto'
import { useInvalidateVcnData } from '../../components/VcnManagement/hooks/useInvalidateVcnData'
import type {
  PaginationState,
  VcnDashboardMetric,
  VcnManagementBenefitFilters,
  VcnManagementBenefitRow,
} from '../../components/VcnManagement/types'
import { countVcnManagementBenefitActiveFilters } from '../../components/VcnManagement/filterVcnManagementBenefitRows'
import { useVcnManagementList } from '../../components/VcnManagement/hooks/useVcnManagementList'
import { useVcnMigrationSummary } from '../../components/VcnManagement/hooks/useVcnMigrationSummary'
import { VcnManagementMigrationCards } from '../../components/VcnManagement/VcnManagementMigrationCards'
import {
  BENEFIT_METRIC_LABELS,
  VcnManagementMetricBanner,
} from '../../components/VcnManagement/VcnManagementMetricBanner'
import { DEFAULT_VCN_MANAGEMENT_FILTERS } from '../../components/VcnManagement/vcnManagementFilterDefaults'
import {
  fetchAllVcnManagementRows,
  terminateVcnSelected,
  terminateAndNewVcnSelected,
  createNewVcnSelected,
} from '../../api/vcn-management-api-client'
import type { VcnSelectionResponse } from '../../api/dto/vcn-selection.dto'

function escapeCsvField(value: string | undefined): string {
  const str = value ?? ''
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function downloadVcnCsv(
  rows: VcnManagementBenefitRow[],
  filename: string
): void {
  const lines = [
    'benefitId,companyName,carrierName,insuredPersonName,primaryPaymentMethod,provider,vcnStatus,vcnToken,lastFourDigits',
    ...rows.map((r) =>
      [
        escapeCsvField(r.benefitId),
        escapeCsvField(r.companyName),
        escapeCsvField(r.carrierName),
        escapeCsvField(r.insuredPersonName),
        escapeCsvField(r.primaryPaymentMethod),
        escapeCsvField(r.provider),
        escapeCsvField(r.vcnStatus),
        escapeCsvField(r.vcnToken),
        escapeCsvField(r.lastFourDigits),
      ].join(',')
    ),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

/**
 * Benefit Mode — one row per benefit.
 *
 * Lifted out of `VcnManagement.tsx` UNCHANGED when the two-tab split landed
 * (EMS-9479): this is the shipped behaviour, moved rather than edited, so the
 * split cannot regress it. Card Mode is a sibling tab with its own state —
 * the tabs replaced a row-unit switch, which is why there are no
 * reset-on-switch rules to share between them.
 */
export interface VcnManagementBenefitTabProps {
  /** Bulk-job state is owned by the page, not this tab: the job outlives a tab
   * switch, so its polling and progress modal cannot live in a pane that gets
   * unmounted. */
  bulkJob: VcnBulkJobStatus | null
  isJobActive: boolean
  refreshBulkJob: () => Promise<unknown> | void
  /** Opens the page-level progress modal — used both when a job starts here and
   * when the admin clicks Bulk upload while one is already running. */
  onShowJobProgress: () => void
}

export function VcnManagementBenefitTab({
  bulkJob,
  isJobActive,
  refreshBulkJob,
  onShowJobProgress,
}: VcnManagementBenefitTabProps) {
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const [filters, setFilters] = useState<VcnManagementBenefitFilters>(() => ({
    ...DEFAULT_VCN_MANAGEMENT_FILTERS,
  }))
  const [searchInput, setSearchInput] = useState('')
  const providerAvailability = useVcnProviderAvailability()
  const { hasAnyProvider } = providerAvailability
  const [selectedRows, setSelectedRows] = useState<VcnManagementBenefitRow[]>(
    []
  )
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isActioning, setIsActioning] = useState(false)

  const selectedRowKeys = useMemo(
    () => selectedRows.map((r) => r.benefitId),
    [selectedRows]
  )
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
  })

  const {
    data: listData,
    isPending: isListPending,
    isFetching: isListFetching,
    isRefetching: isListRefetching,
    error: listError,
  } = useVcnManagementList(filters, pagination.page, pagination.pageSize)

  const {
    data: migrationSummary,
    isPending: isSummaryLoading,
    error: summaryError,
  } = useVcnMigrationSummary()

  const activeMetric = filters.dashboardMetric ?? null

  const invalidateVcnData = useInvalidateVcnData()

  useEffect(() => {
    if (listData?.total === undefined) return
    const total = listData.total
    setPagination((prev) => {
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize))
      const page = prev.page > maxPage ? 1 : prev.page
      if (prev.total === total && prev.page === page) return prev
      return { ...prev, total, page }
    })
  }, [listData?.total])

  const rows = listData?.items ?? []
  const isLoading = isListPending
  const isBusy = isRefetching || isListFetching
  const error = listError instanceof Error ? listError : null

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => {
        const safePage = pageSize !== prev.pageSize ? 1 : page
        return { ...prev, page: safePage, pageSize }
      })
    },
    []
  )

  const handleSearchSubmit = useCallback((value: string) => {
    const trimmed = value.trim()
    setSearchInput(trimmed)
    setFilters((prev) => ({
      ...prev,
      search: trimmed || undefined,
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
    setSelectedRows([])
  }, [])

  const handleRefresh = useCallback(() => {
    setIsRefetching(true)
    const minDelay = new Promise<void>((resolve) => {
      window.setTimeout(resolve, 400)
    })
    void Promise.all([refreshBulkJob(), invalidateVcnData(), minDelay]).finally(
      () => {
        if (mountedRef.current) {
          setIsRefetching(false)
        }
      }
    )
  }, [invalidateVcnData, refreshBulkJob])

  const handleBulkUpload = useCallback(() => {
    if (isJobActive || bulkJob?.state === VcnBulkJobState.TIMEOUT) {
      onShowJobProgress()
    } else {
      setBulkUploadOpen(true)
    }
  }, [isJobActive, bulkJob?.state, onShowJobProgress])

  const handleSidebarApply = useCallback(
    (next: VcnManagementBenefitFilters) => {
      if (activeMetric !== null) return
      setFilters(next)
      setPagination((prev) => ({ ...prev, page: 1 }))
      setSelectedRows([])
    },
    [activeMetric]
  )

  const handleSelectMetric = useCallback(
    (metric: VcnDashboardMetric) => {
      // Re-clicking the active card is intentionally a no-op. Dashboard-metric
      // mode is cleared via VcnManagementMetricBanner, not by toggling the card.
      if (metric === activeMetric) return
      // A dashboard metric is a special filter: it replaces the normal filters.
      setFilterDrawerOpen(false)
      setFilters({ dashboardMetric: metric })
      setSearchInput('')
      setPagination((prev) => ({ ...prev, page: 1 }))
      setSelectedRows([])
    },
    [activeMetric]
  )

  const handleClearMetric = useCallback(() => {
    setFilters({ ...DEFAULT_VCN_MANAGEMENT_FILTERS })
    setSearchInput('')
    setPagination((prev) => ({ ...prev, page: 1 }))
    setSelectedRows([])
  }, [])

  const handleJobStarted = useCallback(() => {
    setBulkUploadOpen(false)
    onShowJobProgress()
    void refreshBulkJob()
    invalidateVcnData()
  }, [invalidateVcnData, onShowJobProgress, refreshBulkJob])

  const handleExportSelected = useCallback(() => {
    if (selectedRows.length === 0) return
    downloadVcnCsv(selectedRows, 'vcn-management-selected.csv')
  }, [selectedRows])

  const handleExportAll = useCallback(async () => {
    if (pagination.total > EXPORT_ALL_LIMIT) return
    setIsExporting(true)
    try {
      const allRows = await fetchAllVcnManagementRows(
        { ...filters },
        EXPORT_ALL_LIMIT
      )
      downloadVcnCsv(allRows, 'vcn-management-all.csv')
    } catch {
      void message.error('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }, [filters, pagination.total])

  const showActionResult = useCallback(
    (result: VcnSelectionResponse, actionLabel: string) => {
      if (result.failedCount === 0) {
        void message.success(
          `${actionLabel}: ${result.successCount} VCN(s) processed successfully.`
        )
        return
      }
      const failedIds = result.results
        .filter((r) => !r.success)
        .map((r) => `${r.benefitId}: ${r.errorMessage ?? 'unknown error'}`)
        .join('\n')
      Modal.warning({
        title: `${actionLabel} — partial failure`,
        content: (
          <div>
            <p>
              {result.successCount} succeeded, {result.failedCount} failed:
            </p>
            <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
              {failedIds}
            </pre>
          </div>
        ),
        width: 520,
      })
    },
    []
  )

  const runSelectionAction = useCallback(
    async (
      benefitIds: string[],
      apiFn: (ids: string[]) => Promise<VcnSelectionResponse>,
      actionLabel: string
    ) => {
      setIsActioning(true)
      try {
        const result = await apiFn(benefitIds)
        showActionResult(result, actionLabel)
        setSelectedRows([])
        invalidateVcnData()
      } catch (e) {
        void message.error(`${actionLabel} failed. Please try again.`)
        throw e
      } finally {
        if (mountedRef.current) setIsActioning(false)
      }
    },
    [invalidateVcnData, showActionResult]
  )

  const handleTerminateSelected = useCallback(() => {
    const benefitIds = selectedRows.map((r) => r.benefitId)
    Modal.confirm({
      title: `Terminate ${benefitIds.length} VCN(s)?`,
      content:
        'This will deactivate the selected virtual cards. This cannot be undone.',
      okText: 'Terminate',
      okButtonProps: { danger: true },
      onOk: () =>
        runSelectionAction(benefitIds, terminateVcnSelected, 'Terminate'),
    })
  }, [runSelectionAction, selectedRows])

  const handleTerminateAndNewSelected = useCallback(() => {
    const benefitIds = selectedRows.map((r) => r.benefitId)
    let provider =
      providerAvailability.enabledProviders[0] ?? VirtualCardType.WEX
    Modal.confirm({
      title: `Terminate and New for ${benefitIds.length} VCN(s)?`,
      content: (
        <div>
          This will deactivate the selected cards and provision new VCNs with
          the chosen provider. This cannot be undone.
          <VcnProviderRadio
            availability={providerAvailability}
            onChange={(value) => (provider = value)}
          />
        </div>
      ),
      okText: 'Terminate and New',
      // Both providers switched off — no card can be issued (EMS-9720).
      okButtonProps: { danger: true, disabled: !hasAnyProvider },
      onOk: () =>
        runSelectionAction(
          benefitIds,
          (ids) => terminateAndNewVcnSelected(ids, provider),
          'Terminate and New'
        ),
    })
  }, [runSelectionAction, selectedRows, providerAvailability, hasAnyProvider])

  const handleCreateNewSelected = useCallback(() => {
    const benefitIds = selectedRows.map((r) => r.benefitId)
    let provider =
      providerAvailability.enabledProviders[0] ?? VirtualCardType.WEX
    Modal.confirm({
      title: `Create New VCN for ${benefitIds.length} benefit(s)?`,
      content: (
        <div>
          This will provision a new VCN for each selected benefit with the
          chosen provider. Existing cards are not terminated.
          <VcnProviderRadio
            availability={providerAvailability}
            onChange={(value) => (provider = value)}
          />
        </div>
      ),
      okText: 'Create New',
      okButtonProps: { disabled: !hasAnyProvider },
      onOk: () =>
        runSelectionAction(
          benefitIds,
          (ids) => createNewVcnSelected(ids, provider),
          'Create New'
        ),
    })
  }, [runSelectionAction, selectedRows, providerAvailability, hasAnyProvider])

  const activeFilterCount = useMemo(
    () => countVcnManagementBenefitActiveFilters(filters),
    [filters]
  )

  const emptyDescription = activeMetric
    ? 'No VCN rows match this dashboard metric. Use Clear filter above to return to the full list.'
    : 'No VCN rows match the current filters. Try adjusting search or filters.'

  return (
    <>
      <VcnManagementMigrationCards
        summary={migrationSummary}
        isLoading={isSummaryLoading}
        error={summaryError instanceof Error ? summaryError : null}
        activeMetric={activeMetric}
        onSelect={handleSelectMetric}
      />
      <VcnManagementTopbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearchSubmit={handleSearchSubmit}
        activeFilterCount={activeFilterCount}
        onToggleFilters={() => setFilterDrawerOpen(true)}
        onRefresh={handleRefresh}
        onBulkUpload={handleBulkUpload}
        onExportSelected={handleExportSelected}
        onExportAll={() => {
          void handleExportAll()
        }}
        onTerminateSelected={handleTerminateSelected}
        onTerminateAndNewSelected={handleTerminateAndNewSelected}
        onCreateNewSelected={handleCreateNewSelected}
        selectedCount={selectedRows.length}
        total={pagination.total}
        isBusy={isBusy}
        isExporting={isExporting}
        isActioning={isActioning}
        isBulkJobActive={isJobActive}
        metricFilterActive={activeMetric !== null}
      />
      {activeMetric && (
        <VcnManagementMetricBanner
          label={BENEFIT_METRIC_LABELS[activeMetric]}
          onClear={handleClearMetric}
        />
      )}
      <VcnManagementContent
        rows={rows}
        isLoading={isLoading}
        isRefetching={isRefetching || isListRefetching}
        error={error}
        selectedRows={selectedRows}
        selectedRowKeys={selectedRowKeys}
        pagination={pagination}
        emptyDescription={emptyDescription}
        onRowSelectionChange={setSelectedRows}
        onPaginationChange={handlePaginationChange}
      />
      <VcnManagementBenefitSidebar
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        appliedFilters={filters}
        onApply={handleSidebarApply}
      />
      <VcnManagementBulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onJobStarted={handleJobStarted}
      />
    </>
  )
}
